import { motion, useScroll, useTransform } from "framer-motion";
import { Award, Users, BookOpen } from "lucide-react";
import { useRef } from "react";
import { ClipReveal, BlurIn, StaggerContainer, StaggerItem } from "./ScrollAnimations";

const stats = [
  { icon: BookOpen, value: "50+", label: "Diary Variants" },
  { icon: Users, value: "1000+", label: "Happy Clients" },
  { icon: Award, value: "4.6", label: "Google Rating" },
];

const CountUp = ({ value }) => {
  const num = parseInt(value);
  if (isNaN(num)) return <span>{value}</span>;
  const suffix = value.replace(/[0-9]/g, "");
  return (
    <motion.span
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
    >
      <motion.span
        initial={{ opacity: 0, scale: 0.5 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
      >
        {value}
      </motion.span>
    </motion.span>
  );
};

export const AboutSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const badgeScale = useTransform(scrollYProgress, [0.2, 0.5], [0.8, 1]);

  return (
    <section ref={sectionRef} id="about" data-testid="about-section" className="py-24 sm:py-32 bg-[#FAFAF7] relative overflow-hidden">
      <motion.div
        className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-[#C8A23A]/5 blur-3xl"
        animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      />
      <motion.div
        className="absolute -bottom-16 -right-16 w-60 h-60 rounded-full bg-[#7A1A31]/5 blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.25, 0.45, 0.25] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        aria-hidden="true"
      />

      <div className="container mx-auto px-6 sm:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image with parallax + clip reveal */}
          <div className="relative">
            <ClipReveal>
              <motion.div style={{ y: imageY }} className="relative overflow-hidden rounded-2xl shadow-2xl shadow-black/10">
                <img src="/about_us.png" alt="Emcee Sons Showroom" data-testid="about-image" className="w-full h-[420px] object-cover" />
                <motion.div
                  className="absolute inset-0 bg-[#C8A23A]/10"
                  initial={{ opacity: 1 }}
                  whileInView={{ opacity: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, delay: 0.5 }}
                />
              </motion.div>
            </ClipReveal>
            <motion.div
              style={{ scale: badgeScale }}
              initial={{ opacity: 0, y: 30, rotate: -8 }}
              whileInView={{ opacity: 1, y: 0, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.6, type: "spring", stiffness: 120 }}
              className="absolute -bottom-6 -right-4 sm:right-6 bg-[#7A1A31] p-6 rounded-xl shadow-xl shadow-[#7A1A31]/20"
            >
              <span className="text-3xl font-bold text-white block" style={{ fontFamily: "'Playfair Display', serif" }}>49+</span>
              <span className="text-xs tracking-[0.15em] uppercase text-white/70" style={{ fontFamily: "'Outfit', sans-serif" }}>Years Since 1976</span>
            </motion.div>
          </div>

          {/* Text content with staggered blur-in */}
          <div>
            <BlurIn delay={0.1}>
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C8A23A] mb-4 block" style={{ fontFamily: "'Outfit', sans-serif" }}>About Us</span>
            </BlurIn>
            <BlurIn delay={0.2}>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-[#1A1A1A] mb-8" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="about-heading">
                Bengaluru's Trusted Name in <br /><span className="text-[#7A1A31]">Premium Diaries</span>
              </h2>
            </BlurIn>
            <BlurIn delay={0.3}>
              <p className="text-base text-[#6B7280] leading-relaxed mb-6" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }} data-testid="about-description">
                Emcee Sons is the Super Stockist for Eagle Diaries, Bengaluru's leading corporate gift supplier specialising in premium New Year diaries, planners, and journals. With our showroom and customisation unit on the iconic Avenue Road since 1976, we bring together decades of craftsmanship and modern design to deliver products that leave a lasting impression.
              </p>
            </BlurIn>
            <BlurIn delay={0.4}>
              <p className="text-base text-[#6B7280] leading-relaxed mb-10" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
                From gold-foiled executive diaries to fully customised corporate stationery, every product reflects our commitment to quality and detail.
              </p>
            </BlurIn>

            <StaggerContainer className="grid grid-cols-3 gap-6" staggerDelay={0.15}>
              {stats.map((stat, i) => (
                <StaggerItem key={i} className="text-center sm:text-left" data-testid={`about-stat-${i}`}>
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <stat.icon className="w-5 h-5 text-[#C8A23A] mb-3 mx-auto sm:mx-0" />
                  </motion.div>
                  <span className="text-2xl font-bold text-[#1A1A1A] block" style={{ fontFamily: "'Playfair Display', serif" }}>
                    <CountUp value={stat.value} />
                  </span>
                  <span className="text-xs text-[#6B7280] tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>{stat.label}</span>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </div>
    </section>
  );
};
